import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography } from 'antd';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import { IProject } from 'models/project';
import YAxisLabelTwoLines from './YAxisLabelTwoLines';

const { Text } = Typography;

interface FMMDataPoint {
    slot: number;
    phase1: number;
    phase2: number;
    phase3: number;
    total: number;
}

interface MagnetomotiveForceDashboardProps {
    project: IProject;
}

// Lines managed by the legend toggle
const LINE_KEYS = ['phase1', 'phase2', 'phase3', 'total'] as const;
type LineKey = typeof LINE_KEYS[number];

const LINE_META: Record<LineKey, { name: string; stroke: string; strokeWidth: number; strokeDasharray?: string }> = {
    phase1: { name: 'FMM Phase 1', stroke: '#e74c3c', strokeWidth: 2, strokeDasharray: '6 6' },
    phase2: { name: 'FMM Phase 2', stroke: '#00a854', strokeWidth: 2, strokeDasharray: '4 4' },
    phase3: { name: 'FMM Phase 3', stroke: '#4f86ff', strokeWidth: 2, strokeDasharray: '3 3' },
    total:  { name: 'Total FMM',   stroke: '#111213', strokeWidth: 4 },
};

// ── Style tokens (matching reference component) ───────────────────────────────
const TEXT_COLOR  = '#111827';
const GRID_COLOR  = '#E5E7EB';
const AXIS_STROKE = '#111827';

const MagnetomotiveForceDashboard: React.FC<MagnetomotiveForceDashboardProps> = ({ project }) => {
    const [selectedSlot, setSelectedSlot] = useState<number>(-1);
    const [hiddenLines, setHiddenLines] = useState<Set<LineKey>>(new Set());

    // Reset on project change
    useEffect(() => {
        setSelectedSlot(-1);
        setHiddenLines(new Set());
    }, [project?.id]);

    // ── Data ─────────────────────────────────────────────────────────────────
    const data = useMemo((): FMMDataPoint[] => {
        const phase1 = project.results?.mmf_phases?.[0] ?? [];
        const phase2 = project.results?.mmf_phases?.[1] ?? [];
        const phase3 = project.results?.mmf_phases?.[2] ?? [];
        const total  = project.results?.mmf_total        ?? [];

        const n = Math.min(phase1.length, phase2.length, phase3.length, total.length);

        return Array.from({ length: n }, (_, i) => ({
            slot: i,
            phase1: phase1[i],
            phase2: phase2[i],
            phase3: phase3[i],
            total:  total[i],
        }));
    }, [project]);

    // ── Derived axis ranges ───────────────────────────────────────────────────
    const maxSlot = data.length > 0 ? data.length - 1 : 0;

    const { yMin, yMax, yTicks } = useMemo(() => {
        if (data.length === 0) return { yMin: -1.1, yMax: 1.1, yTicks: [-1, -0.5, 0, 0.5, 1] };

        const allVals = data.flatMap(d => [d.phase1, d.phase2, d.phase3, d.total]);
        const yMin = Math.floor(Math.min(...allVals) * 10) / 10 - 0.1;
        const yMax = Math.ceil( Math.max(...allVals) * 10) / 10 + 0.1;
        const step = (yMax - yMin) / 4;
        const yTicks = Array.from({ length: 5 }, (_, i) =>
            Math.round((yMin + i * step) * 1000) / 1000,
        );
        return { yMin, yMax, yTicks };
    }, [data]);

    // ── Legend click handler ──────────────────────────────────────────────────
    const handleLegendClick = (e: any) => {
        const key = (Object.entries(LINE_META).find(([, v]) => v.name === e.value)?.[0]) as LineKey | undefined;
        if (!key) return;
        setHiddenLines(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Card style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 16, color: TEXT_COLOR }}>Magnetomotive Force</Text>

            <div style={{ width: '100%', height: 340, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 10, right: 10, bottom: 20, left: 40 }}
                        onClick={(e: any) =>
                            e?.activeLabel !== undefined && setSelectedSlot(Number(e.activeLabel))
                        }
                    >
                        {/* Grid: solid lines, horizontal only — matches reference */}
                        <CartesianGrid
                            strokeDasharray=""
                            stroke="#D1D5DB"
                            strokeOpacity={0.8}
                            vertical={false}
                        />

                        {/* X-axis */}
                        <XAxis
                            dataKey="slot"
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            tick={false}
                            label={{
                                value: 'Slots',
                                position: 'insideBottom',
                                dy: 14,
                                fill: AXIS_STROKE,
                                fontSize: 12,
                                textAnchor: 'middle',
                            }}
                            domain={[0, maxSlot]}
                        />

                        {/* Y-axis */}
                        <YAxis
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            label={{
                                content: (props: any) => (
                                    <YAxisLabelTwoLines
                                        {...props}
                                        lines={['Normalized', 'Magnetomotive Force']}
                                        fill={AXIS_STROKE}
                                        fontSize={12}
                                        dy={56}
                                        textAnchor="middle"
                                    />
                                ),
                            }}
                            domain={[yMin, yMax]}
                            ticks={yTicks}
                            tickFormatter={(v: number) =>
                                Number.isFinite(v) ? v.toFixed(1) : String(v)
                            }
                            tick={{ fontSize: 12, fill: AXIS_STROKE }}
                        />

                        {/* Tooltip */}
                        <Tooltip
                            cursor={{ stroke: 'rgba(0,0,0,0.04)', strokeWidth: 40 }}
                            contentStyle={{
                                backgroundColor: '#ffffff',
                                border: `1px solid ${GRID_COLOR}`,
                                color: TEXT_COLOR,
                                borderRadius: 10,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                            }}
                            labelStyle={{ color: TEXT_COLOR }}
                            content={({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const vals = payload.reduce((acc: any, p: any) => {
                                    acc[p.name] = p.value;
                                    return acc;
                                }, {});
                                return (
                                    <div
                                        style={{
                                            background: '#ffffff',
                                            padding: 10,
                                            borderRadius: 10,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                            border: `1px solid ${GRID_COLOR}`,
                                        }}
                                    >
                                        <div style={{ color: '#e74c3c' }}>FMM Phase 1: {Number(vals['FMM Phase 1']).toFixed(3)}</div>
                                        <div style={{ color: '#00a854' }}>FMM Phase 2: {Number(vals['FMM Phase 2']).toFixed(3)}</div>
                                        <div style={{ color: '#4f86ff' }}>FMM Phase 3: {Number(vals['FMM Phase 3']).toFixed(3)}</div>
                                        <div style={{ fontWeight: 700, marginTop: 6, color: TEXT_COLOR }}>
                                            Total FMM: {Number(vals['Total FMM']).toFixed(3)}
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {/* Legend */}
                        <Legend
                            verticalAlign="top"
                            wrapperStyle={{ cursor: 'pointer', paddingBottom: 8 }}
                            onClick={handleLegendClick}
                            formatter={(value, entry: any) => (
                                <span
                                    style={{
                                        color: hiddenLines.has(
                                            Object.entries(LINE_META).find(([, v]) => v.name === value)?.[0] as LineKey
                                        ) ? '#ccc' : entry.color,
                                        textDecoration: hiddenLines.has(
                                            Object.entries(LINE_META).find(([, v]) => v.name === value)?.[0] as LineKey
                                        ) ? 'line-through' : 'none',
                                        fontSize: 12,
                                    }}
                                >
                                    {value}
                                </span>
                            )}
                        />

                        {selectedSlot >= 0 && (
                            <ReferenceLine
                                x={selectedSlot}
                                stroke="#7f8a94"
                                strokeDasharray="4 2"
                                strokeWidth={1.5}
                            />
                        )}

                        {LINE_KEYS.map(key => (
                            <Line
                                key={key}
                                type="stepAfter"
                                dataKey={key}
                                name={LINE_META[key].name}
                                stroke={LINE_META[key].stroke}
                                strokeWidth={LINE_META[key].strokeWidth}
                                strokeDasharray={LINE_META[key].strokeDasharray}
                                dot={false}
                                hide={hiddenLines.has(key)}
                                isAnimationActive={false}
                            />
                        ))}

                    </LineChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default MagnetomotiveForceDashboard;
